import * as htmlToImage from "html-to-image";
import { DateTime } from "luxon";
import { Tooltip } from "radix-ui";
import { Trans, useTranslation } from "react-i18next";
import { HiOutlineClipboardCopy, HiOutlineDownload } from "react-icons/hi";

import { SvButton } from "@/components";
import { SvInput } from "@/components/SvInput/SvInput.tsx";

type SvDialogShareProps = {
  riding: string;
  date: DateTime<boolean>;
};

export const SvDialogShare = ({ riding, date }: SvDialogShareProps) => {
  const { t } = useTranslation();

  function handleCopy() {
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(window.location.href).catch(err => {
        console.error(err);
      });
    }
  }

  async function downloadFile(url: string, name: string) {
    if (url) {
      try {
        const blob = await fetch(url).then(r => r.blob());
        const fileURL = URL.createObjectURL(blob);
        const downloadLink = document.createElement("a");
        downloadLink.href = fileURL;
        downloadLink.download = `${name ?? "unnamed_file"}.${blob.type.split("/").pop()?.split(".")?.pop() ?? ".txt"}`;
        document.body.appendChild(downloadLink);
        downloadLink.click();
        URL.revokeObjectURL(fileURL);
      } catch (e) {
        console.error(e);
      }
    }
  }

  function handleImage() {
    const node = document.getElementById("riding-projections-container");
    const fileName = `${riding} - ${date.toFormat("yyyy-LLL-dd")}`;

    function filter(node: HTMLElement) {
      const classes = node.classList;
      if (classes) {
        return ![...classes].includes("image-ignore");
      }
      return true;
    }

    if (node) {
      htmlToImage
        .toPng(node, { filter: filter, skipFonts: true })
        .then(dataUrl => {
          void downloadFile(dataUrl, fileName);
        })
        .catch(err => {
          console.error("Error creating image", err);
        });
    } else {
      console.error("Chart node not found");
    }
  }

  function shareOnline() {
    const shareData = {
      title: "SmartVoting.ca",
      text: riding,
      url: window.location.href,
    };

    if (navigator.canShare(shareData)) {
      void navigator.share(shareData);
    } else {
      console.error("Sorry, this browser does not support online sharing.");
    }
  }

  const canShare = navigator?.canShare
    ? navigator.canShare({ url: window.location.href })
    : false;

  return (
    <div className="flex h-max w-full flex-col items-stretch gap-4">
      <div className="flex w-full flex-row items-center justify-stretch gap-2">
        <SvInput
          className="h-10 w-full"
          tabIndex={-1}
          value={window.location.href}
          disabled
          aria-readonly
        />
        <SvButton
          color="primary"
          className="min-h-10 w-max min-w-10 gap-1"
          onClick={handleCopy}>
          <div className="flex h-max w-max min-w-10 shrink-0 grow flex-row items-center gap-1">
            <Trans i18nKey="share.copy" />
            <HiOutlineClipboardCopy className="max-2xs:hidden h-5 w-5 shrink-0" />
          </div>
        </SvButton>
      </div>

      <div className="flex w-full flex-row items-center justify-stretch gap-2">
        {!canShare && (
          <Tooltip.Provider>
            <Tooltip.Root>
              <Tooltip.Trigger asChild>
                <SvButton color="neutral" variant="outline" disabled>
                  <div className="h-10 w-full min-w-max flex-1 shrink !px-2">
                    {t("share.online")}
                  </div>
                </SvButton>
              </Tooltip.Trigger>
              <Tooltip.Portal>
                <Tooltip.Content
                  className="rounded-lg bg-white px-2 py-1"
                  style={{ boxShadow: "1px 0 4px 1px #00000040" }}>
                  <p className="text-xs">{`This browser doesn't support online sharing.`}</p>
                  <Tooltip.Arrow className="fill-white" />
                </Tooltip.Content>
              </Tooltip.Portal>
            </Tooltip.Root>
          </Tooltip.Provider>
        )}

        {canShare && (
          <SvButton
            color="neutral"
            variant="outline"
            disabled={false}
            onClick={shareOnline}>
            <div className="h-10 w-full min-w-max flex-1 shrink !px-2">
              {t("share.online")}
            </div>
          </SvButton>
        )}

        <SvButton
          color="neutral"
          variant="filled"
          className="flex h-10 w-full flex-1 shrink flex-row !px-2"
          onClick={handleImage}>
          <div className="flex h-max min-w-max flex-row items-center gap-2">
            {t("share.image")}
            <HiOutlineDownload className="max-2xs:hidden h-5 w-5 shrink-0" />
          </div>
        </SvButton>
      </div>
    </div>
  );
};
